import {
  CheckCircleOutlined,
  EditOutlined,
  IdcardOutlined,
  MailOutlined,
  StopOutlined,
  TeamOutlined,
  UserOutlined,
} from '@ant-design/icons';
import {
  Avatar,
  Button,
  Modal,
  Popconfirm,
  Space,
  Table,
  Tag,
  Tooltip,
  Typography,
  message,
  theme,
} from 'antd';
import AdministradorClientsForm from 'components/forms/Administradores/AdministradorClientsForm';
import AdminUserForm from 'components/forms/Administradores/AdminUserForm';
import useTenantFlags from 'hooks/useTenantFlags';
import dayjs from 'libs/dayjs';
import { useAdministradoresProvider } from 'providers/Administradores/Administradores';
import { useUserActions } from 'providers/Users/UserActions';
import React, { useCallback, useMemo, useState } from 'react';

const { Text } = Typography;
const { useToken } = theme;

// Lista en formato Table para alinear con TenantsPage.jsx y el resto de
// pantallas de gestión. Reemplaza el Card-per-row anterior (AdminItemList).
const AdministradoresList = ({ refresh }) => {
  const { token } = useToken();
  const { readOnly } = useTenantFlags();
  const { data, loading } = useAdministradoresProvider();
  const { toggleStatus, toggleLoading } = useUserActions();

  const [clientsTarget, setClientsTarget] = useState(null);
  const [editTarget, setEditTarget] = useState(null);

  const handleToggleStatus = useCallback(
    async (row) => {
      try {
        await toggleStatus({ id: row.User.id });
        message.success(row.User.active ? 'Administrador desactivado' : 'Administrador activado');
        refresh?.();
      } catch (err) {
        message.error(err?.response?.data?.mensaje || 'No se pudo cambiar el estado');
      }
    },
    [toggleStatus, refresh]
  );

  const columns = useMemo(
    () => [
      {
        title: 'Administrador',
        key: 'admin',
        render: (_, row) => {
          const { fullname, active } = row.User ?? {};
          return (
            <Space align="center" size={12}>
              <Avatar
                size={36}
                icon={<UserOutlined />}
                style={{
                  backgroundColor: active ? token.colorSuccess : token.colorTextDisabled,
                }}
              />
              <div style={{ minWidth: 0 }}>
                <div style={{ fontWeight: 600, textTransform: 'capitalize' }}>
                  {fullname || '—'}
                </div>
                <Text type="secondary" style={{ fontSize: 12 }}>
                  Ingresó {dayjs(row.createdAt).format('DD MMM YYYY')}
                </Text>
              </div>
            </Space>
          );
        },
      },
      {
        title: 'Contacto',
        key: 'contacto',
        render: (_, row) => {
          const { rut, email } = row.User ?? {};
          return (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Space size={6}>
                <IdcardOutlined style={{ color: token.colorTextTertiary, fontSize: 12 }} />
                <Text style={{ fontSize: 12 }}>{rut || '—'}</Text>
              </Space>
              <Space size={6}>
                <MailOutlined style={{ color: token.colorTextTertiary, fontSize: 12 }} />
                <Text style={{ fontSize: 12 }} copyable={email ? { text: email } : false}>
                  {email || '—'}
                </Text>
              </Space>
            </div>
          );
        },
      },
      {
        title: 'Clientes',
        key: 'clientes',
        width: 110,
        align: 'center',
        render: (_, row) => {
          const count = row.AdminClientes?.length ?? 0;
          return (
            <Tag color={count > 0 ? 'blue' : 'default'} style={{ margin: 0, fontWeight: 600 }}>
              <TeamOutlined /> {count}
            </Tag>
          );
        },
      },
      {
        title: 'Estado',
        key: 'estado',
        width: 100,
        align: 'center',
        render: (_, row) => {
          const active = row.User?.active;
          return (
            <Tag color={active ? 'success' : 'error'} style={{ margin: 0 }}>
              {active ? 'Activo' : 'Inactivo'}
            </Tag>
          );
        },
      },
      {
        title: 'Acciones',
        key: 'actions',
        width: 150,
        align: 'right',
        render: (_, row) => {
          const active = row.User?.active;
          if (readOnly) return null;
          return (
            <Space size={4}>
              <Tooltip title="Gestionar clientes asignados">
                <Button
                  size="small"
                  icon={<TeamOutlined />}
                  onClick={() => setClientsTarget(row)}
                />
              </Tooltip>
              <Tooltip title="Editar datos">
                <Button size="small" icon={<EditOutlined />} onClick={() => setEditTarget(row)} />
              </Tooltip>
              <Popconfirm
                title={
                  active
                    ? `¿Desactivar a ${row.User.fullname}?`
                    : `¿Activar a ${row.User.fullname}?`
                }
                okText="Sí"
                cancelText="No"
                onConfirm={() => handleToggleStatus(row)}
              >
                <Tooltip title={active ? 'Desactivar' : 'Activar'}>
                  <Button
                    size="small"
                    danger={active}
                    loading={toggleLoading}
                    icon={active ? <StopOutlined /> : <CheckCircleOutlined />}
                  />
                </Tooltip>
              </Popconfirm>
            </Space>
          );
        },
      },
    ],
    [token, toggleLoading, handleToggleStatus, readOnly]
  );

  return (
    <>
      <Table
        rowKey="id"
        loading={loading}
        dataSource={data ?? []}
        columns={columns}
        size="middle"
        pagination={{ pageSize: 8, showSizeChanger: false }}
        locale={{ emptyText: 'No se encontraron administradores' }}
      />

      {/* Modal: clientes asignados */}
      <Modal
        open={!!clientsTarget}
        title={
          <Space>
            <TeamOutlined style={{ color: token.colorPrimary }} />
            <span>Clientes asignados a {clientsTarget?.User?.fullname}</span>
          </Space>
        }
        footer={false}
        onCancel={() => setClientsTarget(null)}
        width={800}
        destroyOnHidden
      >
        {clientsTarget && (
          <AdministradorClientsForm
            initialValues={{
              clientes: clientsTarget.AdminClientes?.map((c) => c.idCliente) || [],
              adminId: clientsTarget.id,
            }}
            refresh={() => {
              refresh?.();
              setClientsTarget(null);
            }}
          />
        )}
      </Modal>

      {/* Modal: edición del usuario */}
      <Modal
        open={!!editTarget}
        title={
          <Space>
            <EditOutlined style={{ color: token.colorSuccess }} />
            <span>Editar administrador</span>
          </Space>
        }
        footer={false}
        onCancel={() => setEditTarget(null)}
        width={700}
        destroyOnHidden
      >
        {editTarget && (
          <AdminUserForm
            initialValues={{
              // Migración rut→id: id del usuario (no del administrador) para el update.
              id: editTarget.User.id,
              rut: editTarget.User.rut,
              fullname: editTarget.User.fullname,
              email: editTarget.User.email,
              adminId: editTarget.id,
            }}
            onSuccess={() => {
              refresh?.();
              setEditTarget(null);
            }}
            onCancel={() => setEditTarget(null)}
          />
        )}
      </Modal>
    </>
  );
};

export default AdministradoresList;
